import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  AiMessageThunk,
  ChatFriendsThunk,
  ChatListThunk,
  ChatMediaThunk,
  ChatMembersThunk,
  ChatMessagesThunk,
  CreateAiChatThunk,
  CreateChatThunk,
  CreateGroupChatThunk,
  DeleteChatsThunk,
  LeaveGroupThunk,
  SearchMessagesThunk,
  SendMessageThunk,
  ToggleMuteChatThunk,
  TogglePinChatThunk,
} from "../../middlewares/chat";
import {
  getMessagePreviewText,
  isImagePlaceholderText,
} from "../../../pages/chat/utils";
import { getToken, getUserInfo } from "../../../utils/session";
import { decodeJwtPayload } from "../../../utils/jwt";
import { getSharedPostData } from "../../../utils/sharedPost";
import { isAudioMessage as hasAudioAttachment } from "../../../utils/chatAudio";

const idsMatch = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const normalizeUsername = (value) =>
  String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase();

const getCurrentUsername = () => {
  try {
    const userInfo = getUserInfo();

    return (
      userInfo?.username ||
      userInfo?.user?.username ||
      decodeJwtPayload(getToken()).username ||
      userInfo?.name ||
      userInfo?.email ||
      userInfo?.user?.name ||
      userInfo?.user?.email ||
      ""
    );
  } catch {
    return "";
  }
};

const formatMessageTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizeMessage = (message, chatId) => {
  const sharedPost = getSharedPostData(message);
  const messageType = String(message.type || "TEXT").toUpperCase();
  const fileType = message.file_type || message.fileType || "";
  const fileName =
    message.file_name || message.fileName || message.filename || "";
  const senderUsername =
    message.sender_username ||
    message.s_username ||
    message.from_username ||
    message.username ||
    message.sender?.username ||
    "";
  const receiverUsername =
    message.receiver_username ||
    message.r_username ||
    message.to_username ||
    message.receiver?.username ||
    "";
  const currentUsername = normalizeUsername(getCurrentUsername());
  const normalizedSenderUsername = normalizeUsername(senderUsername);
  const normalizedReceiverUsername = normalizeUsername(receiverUsername);
  const hasExplicitIncoming =
    typeof message.incoming === "boolean" ||
    typeof message.is_incoming === "boolean";
  const explicitIncoming =
    typeof message.incoming === "boolean"
      ? message.incoming
      : Boolean(message.is_incoming);
  const inferredIncoming =
    currentUsername && normalizedSenderUsername
      ? normalizedSenderUsername !== currentUsername
      : currentUsername && normalizedReceiverUsername
        ? normalizedReceiverUsername === currentUsername
        : explicitIncoming;
  const isImageMessage = messageType === "IMAGE" || fileType === "image";
  const isAudioMessage = hasAudioAttachment(message);
  const isDocumentMessage =
    messageType === "DOCUMENT" ||
    fileType === "document" ||
    fileType.startsWith("application") ||
    (messageType === "FILE" && !isImageMessage && !isAudioMessage);
  const isAttachmentMessage =
    isImageMessage ||
    isAudioMessage ||
    isDocumentMessage ||
    Boolean(message.file_url || message.fileUrl);
  const messageContent = message.content || message.text || "";
  const normalizedMessageContent = String(messageContent).trim();
  const isAttachmentPlaceholder =
    isAttachmentMessage &&
    (normalizedMessageContent === String(fileName || "").trim() ||
      normalizedMessageContent.toLowerCase() === "voice message" ||
      (isImageMessage && isImagePlaceholderText(normalizedMessageContent)));
  const sentAt = message.sent_at || message.created_at || "";
  const id =
    message.id ??
    message.temp_id ??
    `${chatId}-${sentAt || message.content || Date.now()}`;
  const systemFlag = message.is_system ?? message.isSystem ?? false;
  const isSystemMessage =
    systemFlag === true ||
    systemFlag === 1 ||
    String(systemFlag).toLowerCase() === "true" ||
    messageType === "SYSTEM" ||
    normalizeUsername(message.author || senderUsername) === "system";

  return {
    id,
    temp_id: message.temp_id || message.client_msg_id || null,
    chat_id: message.chat_id ?? chatId,
    author: message.author || senderUsername || "Unknown",
    sender_username: senderUsername,
    sender_full_name:
      message.sender_full_name || message.sender?.full_name || "",
    receiver_username: receiverUsername,
    content: messageContent,
    // Attachments may carry a user-written caption in `content`. The previous
    // normalizer erased it unconditionally, making file + text look as though
    // only the file had been sent. Hide only generated placeholders (document
    // filename / "Voice message"), not a real caption.
    text: sharedPost
      ? sharedPost.optionalMessage
      : isAttachmentPlaceholder
        ? ""
        : messageContent,
    ...(sharedPost
      ? {
          post_id: sharedPost.postId,
          optional_message: sharedPost.optionalMessage,
          shared_post: sharedPost.post,
        }
      : {}),
    time: message.time || formatMessageTime(sentAt),
    created_at: message.created_at || sentAt,
    sent_at: sentAt,
    type: messageType,
    file_url: message.file_url || message.fileUrl || null,
    file_name: fileName,
    file_type:
      fileType ||
      (isImageMessage
        ? "image"
        : isAudioMessage
          ? "audio"
          : isDocumentMessage
            ? "document"
            : ""),
    file_size: message.file_size || message.fileSize || null,
    duration_ms:
      message.duration_ms ||
      message.durationMs ||
      message.voice_duration_ms ||
      null,
    incoming:
      currentUsername &&
      (normalizedSenderUsername || normalizedReceiverUsername)
        ? Boolean(inferredIncoming)
        : hasExplicitIncoming
          ? explicitIncoming
          : false,
    is_system: isSystemMessage,
    is_ai: Boolean(message.is_ai),
    reply_to_id: message.reply_to_id || null,
    reply_preview:
      message.reply_preview ||
      message.replyPreview ||
      (message.reply_to_content
        ? {
            id: message.reply_to_id,
            text: message.reply_to_content,
          }
        : null),
    isOptimistic: Boolean(message.isOptimistic || message.is_optimistic),
    isPinned: Boolean(message.isPinned || message.is_pinned || message.pinned),
    isSelected: Boolean(message.isSelected || message.is_selected),
    // Whether this (own, outgoing) message has been read by the peer. Persists
    // through history fetch, optimistic send, and socket upserts.
    is_read: Boolean(message.is_read || message.read),
  };
};

const normalizeChat = (chat) => {
  const id = chat.id ?? chat.chat_id;
  const name = chat.name || chat.chat_name || chat.title || "";
  const lastMessage = chat.last_message || chat.preview || "";
  const sharedPreview = getSharedPostData({
    type: "SHARED_POST",
    content: lastMessage,
  });
  const lastMessageTime =
    chat.time ||
    chat.last_message_time ||
    chat.updated_at ||
    chat.created_at ||
    "";

  return {
    id,
    name: name || (id ? `Chat ${id}` : "Untitled chat"),
    type:
      chat.type ||
      (chat.is_group ? "GROUP" : chat.is_ai_chat ? "AI" : "DIRECT"),
    status: chat.status || "",
    time: formatMessageTime(lastMessageTime),
    preview: sharedPreview?.postId
      ? sharedPreview.optionalMessage || "Shared post"
      : lastMessage,
    // Keep generated UI copy distinct from a user's identical literal text.
    // Translate at render time so stored previews follow language changes.
    // /chats currently omits attachment metadata. A last-message timestamp
    // with empty content still represents a message, not an empty chat.
    previewKey:
      sharedPreview?.postId && !sharedPreview.optionalMessage
        ? "profilePage.sharedPost"
        : !lastMessage &&
            !(Array.isArray(chat.messages) && chat.messages.length)
          ? chat.last_message_time
            ? "chat.attachment"
            : "chat.noMessages"
          : null,
    description: chat.description || chat.groupDescription || "",
    avatarUrl: chat.avatarUrl || chat.avatar_url || "",
    isLocal: Boolean(chat.isLocal || chat.is_local),
    unread: Number(chat.unread || chat.unread_count || 0),
    isMuted: Boolean(chat.isMuted || chat.is_muted),
    isPinned: Boolean(chat.isPinned || chat.is_pinned),
    avatarText: chat.avatarText || name?.slice(0, 2)?.toUpperCase() || "CH",
    membersCount:
      chat.membersCount || chat.members_count || chat.member_count || 0,
    onlineMembersCount:
      chat.onlineMembersCount ||
      chat.online_members_count ||
      chat.online_count ||
      0,
    peerUsername:
      chat.peerUsername || chat.peer_username || chat.username || "",
    peerFullName: chat.peerFullName || chat.peer_full_name || "",
    memberUsernames: chat.memberUsernames || chat.member_usernames || [],
    memberFullNames: chat.memberFullNames || chat.member_full_names || [],
    tags: chat.tags || [],
    messages: Array.isArray(chat.messages)
      ? chat.messages.map((message) => normalizeMessage(message, id))
      : [],
  };
};

const sortChats = (items) =>
  [...items].sort((left, right) => {
    if (left.isPinned !== right.isPinned) {
      return left.isPinned ? -1 : 1;
    }

    const leftId = Number(left.id);
    const rightId = Number(right.id);

    if (Number.isFinite(leftId) && Number.isFinite(rightId)) {
      return rightId - leftId;
    }

    return String(right.id).localeCompare(String(left.id));
  });

const initialState = {
  loading: false,
  messageLoading: false,
  messageFetchesByChatId: {},
  actionLoading: false,
  friendsLoading: false,
  latestFriendsRequestId: null,
  membersLoading: false,
  chats: [],
  friends: [],
  chatMembersById: {},
  // Server-backed media galleries, keyed by chat id ->
  // { stats, media, files, posts, loaded, loading }. Populated on info-panel open.
  chatMediaById: {},
  // Server-backed in-conversation search, keyed by chat id ->
  // { query, results, loading }. Whole-history search behind the search panel.
  messageSearchByChatId: {},
  // Who is currently typing, keyed by chat id -> peer username (or absent/null).
  typingByChatId: {},
  // Live online/offline presence, keyed by username -> { status, at }.
  presenceByUsername: {},
  selectedConversationId: null,
  // The backend chat list is cached briefly. Keep successful local deletions
  // hidden until a fresh server list confirms they are gone, so a stale
  // background refresh cannot reinsert a chat the user just deleted.
  locallyDeletedChatIds: [],
  pendingChatDeletions: {},
  filter: "all",
  searchValue: "",
  composerValue: "",
  error: null,
  endpointSummary: {
    chatList: "GET /chats",
    chatMessages: "GET /messages_by_chat_id?chat_id=:id&page=1&page_size=50",
    sendMessage: "WS /ws/chat/:username",
    aiMessage: "POST /ai_message",
    muteChat: "POST /mute_chat/:id",
    deleteChat: "POST /delete_chat/:id",
    createChat: "POST /create_chat",
    createAiChat: "POST /create_ai_chat",
    createGroupChat: "POST /create_group_chat",
    pinChat: "POST /pin_chat/:id",
    chatMembers: "GET /chats/:id/members",
  },
};

const updateChatPreview = (chat) => {
  const lastMessage = chat.messages.at(-1);

  if (!lastMessage) {
    chat.preview = "No messages yet";
    chat.previewKey = "chat.noMessages";
    chat.time = "";
    return;
  }

  chat.preview = getMessagePreviewText(lastMessage);
  chat.previewKey = null;
  chat.time = lastMessage.time || formatMessageTime(lastMessage.sent_at);
};

const upsertChatMessage = (chat, message) => {
  const existingMessage = chat.messages.find(
    (item) =>
      idsMatch(item.id, message.id) || idsMatch(item.temp_id, message.temp_id),
  );

  if (existingMessage) {
    // The server echo can arrive before the send thunk finishes. Keep its
    // confirmed id and timestamp when the matching optimistic result arrives.
    if (!existingMessage.isOptimistic && message.isOptimistic) return;
    Object.assign(existingMessage, {
      ...message,
      isSelected: existingMessage.isSelected,
    });
    return;
  }

  const optimisticMessage = chat.messages.find((item) => {
    const itemSender = item.sender_username || item.author || "";
    const messageSender = message.sender_username || message.author || "";

    return (
      item.isOptimistic &&
      !message.isOptimistic &&
      idsMatch(item.chat_id, message.chat_id) &&
      String(item.type || "") === String(message.type || "") &&
      String(item.content || item.text || "") ===
        String(message.content || message.text || "") &&
      String(item.file_url || "") === String(message.file_url || "") &&
      String(item.reply_to_id || "") === String(message.reply_to_id || "") &&
      itemSender &&
      messageSender &&
      itemSender === messageSender
    );
  });

  if (optimisticMessage) {
    Object.assign(optimisticMessage, {
      ...message,
      isSelected: optimisticMessage.isSelected,
    });
    return;
  }

  chat.messages.push(message);
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setSelectedConversation(state, action) {
      if (!idsMatch(state.selectedConversationId, action.payload)) {
        state.composerValue = "";
      }

      state.selectedConversationId = action.payload;
      const currentChat = state.chats.find((chat) =>
        idsMatch(chat.id, action.payload),
      );

      if (currentChat) {
        currentChat.unread = 0;
      }
    },
    setChatSearch(state, action) {
      state.searchValue = action.payload;
    },
    setChatFilter(state, action) {
      state.filter = action.payload;
    },
    setComposerValue(state, action) {
      state.composerValue = action.payload;
    },
    receiveSocketMessage(state, action) {
      // The chat WebSocket multiplexes many frame types over one channel.
      const frame = action.payload || {};
      const frameType = String(frame.type || "").toLowerCase();

      // Typing indicator. This is the ONLY frame with no `type` field — the server
      // detects it by a bare `typing` key (see websockets.py), so we do too. Track
      // the peer who is typing, keyed by chat id; a `typing: false` clears it.
      if (typeof frame.typing !== "undefined" && !frameType) {
        if (frame.chat_id === null || frame.chat_id === undefined) {
          return;
        }
        const typingKey = String(frame.chat_id);
        if (frame.typing) {
          state.typingByChatId[typingKey] = frame.s_username || "";
        } else {
          delete state.typingByChatId[typingKey];
        }
        return;
      }

      // Presence: a chat participant came online / went offline. Track it and
      // reflect it live on any 1:1 chat with that peer (the header reads
      // chat.status). The backend auto-broadcasts this on socket connect/disconnect.
      if (frameType === "status_update") {
        if (!frame.username) {
          return;
        }
        const status = frame.status === "online" ? "online" : "offline";
        state.presenceByUsername[frame.username] = {
          status,
          at: frame.timestamp || null,
        };
        state.chats.forEach((chat) => {
          if (chat.type === "DIRECT" && chat.peerUsername === frame.username) {
            chat.status = status;
          }
        });
        return;
      }

      // Read receipt: a peer read messages in this chat. Mark OUR own outgoing
      // messages (incoming === false) whose id is listed as read/seen. message_ids
      // arrives as a real array from the server but tolerate a bracketed-CSV string
      // too (the shape Android sends on the wire).
      if (frameType === "read") {
        const readChat = state.chats.find((chat) =>
          idsMatch(chat.id, frame.chat_id),
        );
        if (!readChat) {
          return;
        }
        const rawIds = frame.message_ids;
        const idList = Array.isArray(rawIds)
          ? rawIds
          : String(rawIds || "")
              .replace(/[[\]\s]/g, "")
              .split(",")
              .filter(Boolean);
        const readIds = new Set(idList.map((value) => String(value)));
        readChat.messages.forEach((message) => {
          if (!message.incoming && readIds.has(String(message.id))) {
            message.is_read = true;
          }
        });
        return;
      }

      // Peer message-mutation frames update the matching message live (a peer edits/
      // deletes/pins a message and it reflects on this client). The server sends
      // explicit values, so pin SETS is_pinned rather than toggling, and edit does
      // not touch this client's composer.
      if (
        frameType === "edit_message" ||
        frameType === "delete_message" ||
        frameType === "pin_message"
      ) {
        const targetChat = state.chats.find((chat) =>
          idsMatch(chat.id, frame.chat_id),
        );
        if (!targetChat) {
          return;
        }
        if (frameType === "delete_message") {
          targetChat.messages = targetChat.messages.filter(
            (msg) => !idsMatch(msg.id, frame.message_id),
          );
          updateChatPreview(targetChat);
          return;
        }
        const targetMessage = targetChat.messages.find((msg) =>
          idsMatch(msg.id, frame.message_id),
        );
        if (!targetMessage) {
          return;
        }
        if (frameType === "edit_message") {
          targetMessage.content = frame.content ?? targetMessage.content;
          targetMessage.text = frame.content ?? targetMessage.text;
          targetMessage.isEdited = true;
          updateChatPreview(targetChat);
        } else {
          targetMessage.isPinned = Boolean(frame.is_pinned);
        }
        return;
      }

      // Normal sends broadcast a raw Message with type TEXT/FILE/etc. Shared
      // posts also use a { type: "message", message: ... } envelope. Accept both
      // wire formats while keeping lifecycle/error/keepalive frames out.
      const isMessageType = [
        "text",
        "image",
        "video",
        "audio",
        "file",
        "document",
        "shared_post",
        "system",
      ].includes(frameType);
      if (frameType && frameType !== "message" && !isMessageType) {
        return;
      }

      const payload =
        action.payload?.message ||
        action.payload?.data?.message ||
        action.payload?.data ||
        action.payload;
      const chatId = payload?.chat_id || payload?.chatId;

      if (!chatId) {
        return;
      }

      const message = normalizeMessage(payload, chatId);
      let currentChat = state.chats.find((chat) =>
        idsMatch(chat.id, message.chat_id),
      );

      if (!currentChat) {
        currentChat = normalizeChat({
          chat_id: chatId,
          is_group: payload.is_group,
          is_ai_chat: payload.is_ai_chat,
          chat_name:
            payload.chat_name ||
            (message.incoming
              ? message.sender_username
              : message.receiver_username || message.sender_username),
          username: message.incoming
            ? message.sender_username
            : message.receiver_username || message.sender_username,
          last_message: payload.content,
          last_message_time: payload.sent_at,
          unread_count: payload.unread_count || 0,
          messages: [],
        });
        state.chats.push(currentChat);
      }

      const alreadyReceived = currentChat.messages.some((item) =>
        idsMatch(item.id, message.id),
      );
      upsertChatMessage(currentChat, message);
      updateChatPreview(currentChat);

      if (
        message.incoming &&
        !alreadyReceived &&
        !idsMatch(state.selectedConversationId, message.chat_id)
      ) {
        currentChat.unread += 1;
      }

      state.chats = sortChats(state.chats);
    },
    updateLocalGroupChat(state, action) {
      const { id, name, description, avatarUrl, avatarText } = action.payload;
      const currentChat = state.chats.find((chat) => idsMatch(chat.id, id));

      if (!currentChat || currentChat.type !== "GROUP") {
        return;
      }

      currentChat.name = name || currentChat.name;
      currentChat.description = description ?? currentChat.description;
      currentChat.avatarUrl = avatarUrl ?? currentChat.avatarUrl;
      currentChat.avatarText = avatarText || currentChat.avatarText;
    },
    updateLocalMessage(state, action) {
      const { chatId, messageId, text } = action.payload;
      const currentChat = state.chats.find((chat) => idsMatch(chat.id, chatId));
      const currentMessage = currentChat?.messages.find((message) =>
        idsMatch(message.id, messageId),
      );

      if (!currentMessage) {
        return;
      }

      currentMessage.text = text;
      currentMessage.content = text;
      currentMessage.isEdited = true;
      updateChatPreview(currentChat);
      state.composerValue = "";
    },
    deleteLocalMessage(state, action) {
      const { chatId, messageId } = action.payload;
      const currentChat = state.chats.find((chat) => idsMatch(chat.id, chatId));

      if (!currentChat) {
        return;
      }

      currentChat.messages = currentChat.messages.filter(
        (message) => !idsMatch(message.id, messageId),
      );
      updateChatPreview(currentChat);
    },
    toggleLocalMessagePin(state, action) {
      const { chatId, messageId } = action.payload;
      const currentChat = state.chats.find((chat) => idsMatch(chat.id, chatId));
      const currentMessage = currentChat?.messages.find((message) =>
        idsMatch(message.id, messageId),
      );

      if (currentMessage) {
        currentMessage.isPinned = !currentMessage.isPinned;
      }
    },
    toggleLocalMessageSelect(state, action) {
      const { chatId, messageId } = action.payload;
      const currentChat = state.chats.find((chat) => idsMatch(chat.id, chatId));
      const currentMessage = currentChat?.messages.find((message) =>
        idsMatch(message.id, messageId),
      );

      if (currentMessage) {
        currentMessage.isSelected = !currentMessage.isSelected;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ChatListThunk.pending, (state) => {
        // Keep an already-rendered list visible during background refreshes
        // (for example the new_chat socket event after creating a chat). Hiding
        // it behind the initial loader made the sidebar and route appear to
        // flicker even though the selected chat had not changed.
        state.loading = state.chats.length === 0;
        state.error = null;
      })
      .addCase(ChatListThunk.fulfilled, (state, action) => {
        const normalizedChats = (action.payload || []).map((item) => {
          const chat = normalizeChat(item);
          const previous = state.chats.find((old) => idsMatch(old.id, chat.id));
          if (previous && !Array.isArray(item.messages))
            chat.messages = previous.messages;
          return chat;
        });
        const returnedChatIds = new Set(
          normalizedChats.map((chat) => String(chat.id)),
        );
        const locallyDeletedChatIds = new Set(
          state.locallyDeletedChatIds.map(String),
        );
        const visibleServerChats = normalizedChats.filter(
          (chat) => !locallyDeletedChatIds.has(String(chat.id)),
        );
        const fetchedChatIds = new Set(
          visibleServerChats.map((chat) => String(chat.id)),
        );
        const preservedChats = state.chats.filter(
          (chat) => chat.isLocal && !fetchedChatIds.has(String(chat.id)),
        );

        state.chats = sortChats([...visibleServerChats, ...preservedChats]);
        state.locallyDeletedChatIds = state.locallyDeletedChatIds.filter(
          (chatId) => returnedChatIds.has(String(chatId)),
        );
        state.loading = false;
      })
      .addCase(ChatListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Unable to load chats";
      })
      .addCase(ChatMessagesThunk.pending, (state, action) => {
        const chatId = action.meta?.arg?.chatId;
        if (chatId != null) {
          const chat = state.chats.find((item) => idsMatch(item.id, chatId));
          state.messageFetchesByChatId[String(chatId)] = {
            requestId: action.meta.requestId,
            messageIds: (chat?.messages || []).map((message) =>
              String(message.id),
            ),
          };
        }
        state.messageLoading = true;
      })
      .addCase(ChatMessagesThunk.fulfilled, (state, action) => {
        const { chatId, response } = action.payload;
        const fetch = state.messageFetchesByChatId[String(chatId)];
        if (fetch && fetch.requestId !== action.meta?.requestId) return;
        const currentChat = state.chats.find((chat) =>
          idsMatch(chat.id, chatId),
        );
        const messageItems = response?.items || response?.messages || [];

        if (currentChat) {
          const initialIds = new Set(fetch?.messageIds || []);
          const receivedDuringFetch = fetch
            ? currentChat.messages.filter(
                (message) =>
                  message.isOptimistic || !initialIds.has(String(message.id)),
              )
            : [];
          currentChat.messages = messageItems.map((message) =>
            normalizeMessage(message, chatId),
          );
          receivedDuringFetch.forEach((message) =>
            upsertChatMessage(currentChat, message),
          );
          updateChatPreview(currentChat);
        }

        delete state.messageFetchesByChatId[String(chatId)];
        state.messageLoading =
          Object.keys(state.messageFetchesByChatId).length > 0;
      })
      .addCase(ChatMessagesThunk.rejected, (state, action) => {
        const chatId = String(action.meta?.arg?.chatId);
        if (
          state.messageFetchesByChatId[chatId]?.requestId ===
          action.meta?.requestId
        ) {
          delete state.messageFetchesByChatId[chatId];
        }
        state.messageLoading =
          Object.keys(state.messageFetchesByChatId).length > 0;
      })
      .addCase(SendMessageThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(SendMessageThunk.fulfilled, (state, action) => {
        const sentPayload = action.payload?.request || action.meta?.arg || {};
        const localMetadata = action.payload?.localMetadata || {};
        const response = action.payload?.response || action.payload || {};
        const message = normalizeMessage(
          {
            ...sentPayload,
            ...localMetadata,
            ...response,
            reply_to_id:
              response?.reply_to_id ?? sentPayload.reply_to_id ?? null,
            reply_preview:
              response?.reply_preview ??
              localMetadata.reply_preview ??
              sentPayload.reply_preview ??
              null,
          },
          response?.chat_id || sentPayload.chat_id,
        );
        const currentChat = state.chats.find((chat) =>
          idsMatch(chat.id, message.chat_id),
        );

        if (currentChat) {
          upsertChatMessage(currentChat, message);
          updateChatPreview(currentChat);
          currentChat.unread = 0;
          state.chats = sortChats(state.chats);
        }

        state.composerValue = "";
        state.actionLoading = false;
      })
      .addCase(SendMessageThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(AiMessageThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(AiMessageThunk.fulfilled, (state, action) => {
        const message = normalizeMessage(
          action.payload,
          action.payload.chat_id,
        );
        const currentChat = state.chats.find((chat) =>
          idsMatch(chat.id, message.chat_id),
        );

        if (currentChat) {
          upsertChatMessage(currentChat, message);
          updateChatPreview(currentChat);
          state.chats = sortChats(state.chats);
        }

        state.actionLoading = false;
      })
      .addCase(AiMessageThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(ToggleMuteChatThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(ToggleMuteChatThunk.fulfilled, (state, action) => {
        const currentChat = state.chats.find((chat) =>
          idsMatch(chat.id, action.payload.chatId),
        );

        if (currentChat) {
          currentChat.isMuted = action.payload.shouldMute;
        }

        state.actionLoading = false;
      })
      .addCase(ToggleMuteChatThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(DeleteChatsThunk.pending, (state, action) => {
        // Hide rows immediately, retaining their data until the server confirms
        // deletion so a failed request can simply make them visible again.
        state.pendingChatDeletions[action.meta.requestId] =
          action.meta.arg.chatIds.map(String);
        state.actionLoading = true;
      })
      .addCase(DeleteChatsThunk.fulfilled, (state, action) => {
        delete state.pendingChatDeletions[action.meta?.requestId];
        const deletedChatIds = new Set(action.payload.chatIds.map(String));

        state.locallyDeletedChatIds = Array.from(
          new Set([
            ...state.locallyDeletedChatIds.map(String),
            ...deletedChatIds,
          ]),
        );

        state.chats = state.chats.filter(
          (chat) => !deletedChatIds.has(String(chat.id)),
        );
        deletedChatIds.forEach((chatId) => {
          delete state.chatMembersById[chatId];
          delete state.chatMediaById[chatId];
        });

        if (deletedChatIds.has(String(state.selectedConversationId))) {
          state.selectedConversationId = null;
          state.composerValue = "";
        }

        state.actionLoading =
          Object.keys(state.pendingChatDeletions).length > 0;
      })
      .addCase(LeaveGroupThunk.fulfilled, (state, action) => {
        // Leaving a group drops it from the list (mirrors delete), just server-side.
        const leftId = String(action.payload.chatId);
        state.locallyDeletedChatIds = Array.from(
          new Set([...state.locallyDeletedChatIds.map(String), leftId]),
        );
        state.chats = state.chats.filter((chat) => String(chat.id) !== leftId);
        if (String(state.selectedConversationId) === leftId) {
          state.selectedConversationId = null;
          state.composerValue = "";
        }
      })
      .addCase(DeleteChatsThunk.rejected, (state, action) => {
        delete state.pendingChatDeletions[action.meta?.requestId];
        state.actionLoading =
          Object.keys(state.pendingChatDeletions).length > 0;
      })
      .addCase(CreateChatThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(CreateChatThunk.fulfilled, (state, action) => {
        const response = action.payload.response || {};
        const responseChatId = response.id ?? response.chat_id;
        const isAlreadyLoaded = state.chats.some((chat) =>
          idsMatch(chat.id, responseChatId),
        );
        const chat = normalizeChat({
          ...response,
          isLocal: !isAlreadyLoaded,
        });

        if (!chat.id) {
          state.actionLoading = false;
          return;
        }

        state.chats = sortChats([
          chat,
          ...state.chats.filter((item) => !idsMatch(item.id, chat.id)),
        ]);
        state.locallyDeletedChatIds = state.locallyDeletedChatIds.filter(
          (chatId) => !idsMatch(chatId, chat.id),
        );
        state.selectedConversationId = chat.id;
        state.composerValue = "";
        state.actionLoading = false;
      })
      .addCase(CreateChatThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(CreateAiChatThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(CreateAiChatThunk.fulfilled, (state, action) => {
        const response = action.payload.response || {};
        const responseChatId = response.id ?? response.chat_id;
        const isAlreadyLoaded = state.chats.some((chat) =>
          idsMatch(chat.id, responseChatId),
        );
        const chat = normalizeChat({
          ...response,
          isLocal: !isAlreadyLoaded,
        });

        if (!chat.id) {
          state.actionLoading = false;
          return;
        }

        state.chats = sortChats([
          chat,
          ...state.chats.filter((item) => !idsMatch(item.id, chat.id)),
        ]);
        state.locallyDeletedChatIds = state.locallyDeletedChatIds.filter(
          (chatId) => !idsMatch(chatId, chat.id),
        );
        state.selectedConversationId = chat.id;
        state.composerValue = "";
        state.actionLoading = false;
      })
      .addCase(CreateAiChatThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(CreateGroupChatThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(CreateGroupChatThunk.fulfilled, (state, action) => {
        const response = action.payload.response || {};
        const responseChatId = response.id ?? response.chat_id;
        const isAlreadyLoaded = state.chats.some((chat) =>
          idsMatch(chat.id, responseChatId),
        );
        const chat = normalizeChat({
          ...response,
          isLocal: !isAlreadyLoaded,
        });

        if (!chat.id) {
          state.actionLoading = false;
          return;
        }

        state.chats = sortChats([
          chat,
          ...state.chats.filter((item) => !idsMatch(item.id, chat.id)),
        ]);
        state.selectedConversationId = chat.id;
        state.composerValue = "";
        state.actionLoading = false;
      })
      .addCase(CreateGroupChatThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(TogglePinChatThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(TogglePinChatThunk.fulfilled, (state, action) => {
        const chatIds = new Set((action.payload.chatIds || []).map(String));

        state.chats.forEach((chat) => {
          if (chatIds.has(String(chat.id))) {
            chat.isPinned = action.payload.shouldPin;
          }
        });
        state.chats = sortChats(state.chats);
        state.actionLoading = false;
      })
      .addCase(TogglePinChatThunk.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(ChatFriendsThunk.pending, (state, action) => {
        state.friendsLoading = true;
        state.latestFriendsRequestId = action.meta.requestId;
      })
      .addCase(ChatFriendsThunk.fulfilled, (state, action) => {
        // A relationship change can refresh contacts while an older request
        // is still running. Only the latest request may update the picker.
        if (action.meta.requestId !== state.latestFriendsRequestId) return;
        state.friends = action.payload || [];
        state.friendsLoading = false;
      })
      .addCase(ChatFriendsThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFriendsRequestId) return;
        state.friendsLoading = false;
      })
      .addCase(ChatMembersThunk.pending, (state) => {
        state.membersLoading = true;
      })
      .addCase(ChatMembersThunk.fulfilled, (state, action) => {
        state.chatMembersById[String(action.payload.chatId)] =
          action.payload.response || [];
        state.membersLoading = false;
      })
      .addCase(ChatMembersThunk.rejected, (state) => {
        state.membersLoading = false;
      })
      .addCase(ChatMediaThunk.pending, (state, action) => {
        const key = String(action.meta.arg?.chatId);
        const previous = state.chatMediaById[key] || {};
        state.chatMediaById[key] = { ...previous, loading: true };
      })
      .addCase(ChatMediaThunk.fulfilled, (state, action) => {
        const { chatId, stats, media, files, posts } = action.payload;
        state.chatMediaById[String(chatId)] = {
          stats: stats || null,
          media: media || [],
          files: files || [],
          posts: posts || [],
          loaded: true,
          loading: false,
        };
      })
      .addCase(ChatMediaThunk.rejected, (state, action) => {
        const key = String(action.meta.arg?.chatId);
        const previous = state.chatMediaById[key] || {};
        state.chatMediaById[key] = { ...previous, loading: false };
      })
      .addCase(SearchMessagesThunk.pending, (state, action) => {
        const key = String(action.meta.arg?.chatId);
        const previous = state.messageSearchByChatId[key] || {};
        state.messageSearchByChatId[key] = { ...previous, loading: true };
      })
      .addCase(SearchMessagesThunk.fulfilled, (state, action) => {
        const { chatId, query, response } = action.payload;
        // Normalize with the same mapper as the message list so results render
        // identically and their ids line up with loaded messages for scroll-to.
        state.messageSearchByChatId[String(chatId)] = {
          query,
          results: (Array.isArray(response) ? response : []).map((message) =>
            normalizeMessage(message, chatId),
          ),
          loading: false,
        };
      })
      .addCase(SearchMessagesThunk.rejected, (state, action) => {
        const key = String(action.meta.arg?.chatId);
        const previous = state.messageSearchByChatId[key] || {};
        state.messageSearchByChatId[key] = { ...previous, loading: false };
      });
  },
});

export const {
  deleteLocalMessage,
  receiveSocketMessage,
  setChatFilter,
  setChatSearch,
  setComposerValue,
  setSelectedConversation,
  toggleLocalMessagePin,
  toggleLocalMessageSelect,
  updateLocalMessage,
  updateLocalGroupChat,
} = chatSlice.actions;

export const selectVisibleChats = createSelector(
  [(state) => state.chat.chats, (state) => state.chat.pendingChatDeletions],
  (chats, pendingDeletions) => {
    const pendingIds = new Set(Object.values(pendingDeletions || {}).flat());
    return pendingIds.size
      ? chats.filter((chat) => !pendingIds.has(String(chat.id)))
      : chats;
  },
);

export default chatSlice.reducer;
